import { Component, EventEmitter, Input, OnInit, Output, ViewChild, inject } from "@angular/core";
import { AbstractControl, FormGroup, FormsModule, NgForm } from "@angular/forms";
import { LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { BusyIndicatorComponent } from "@tylertech/common-components-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { isErroredExecutionResult, isExecutionResult, isHttpResponseError } from "@tylertech/core-module";
import { ForgeButtonModule, ForgeCardModule } from "@tylertech/forge-angular";
import { MetadataErrorService, MetadataPanelComponent, RepositoryFormData } from "@tylertech/metadata-module";
import { ClassMetadata, IInfoCardData, IInfoCardSaveCriteria, ISearchCriteria, MetadataVerification } from "@tylertech/tyler-api-types";
import { InfoCardService } from "../../services";

@Component({
    selector: "info-card-edit",
    templateUrl: "./infoCardEdit.tmpl.html",
    imports: [
        BusyIndicatorComponent,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeButtonModule,
        ForgeCardModule,
        FormsModule,
        MetadataPanelComponent,
    ]
})
export class InfoCardEditComponent implements OnInit
{
    private readonly $commonDialogsService = inject(CommonDialogsService);
    private readonly $metadataErrorService = inject(MetadataErrorService);
    private $infoCardService = inject(InfoCardService);

    @Input() private readonly infoCard: IInfoCardData;
    @Input() private readonly returnCriteria: ISearchCriteria<IInfoCardData>;

    @Input() readonly outlined: boolean;
    // TODO - Why would be allowed to enter the "Edit" mode if we can't save???
    @Input() readonly saveable: boolean;

    @Output() private readonly save = new EventEmitter<IInfoCardData>();
    @Output() private readonly cancel = new EventEmitter<void>();

    @ViewChild("form") form: NgForm;

    isLoaded: boolean;

    get formValid(): boolean { return this.form?.valid || true; }
    get entity(): unknown { return this.infoCard?.Entity; }
    get metadata(): ClassMetadata { return this.infoCard?.Metadata; }

    ngOnInit(): void
    {
        this.setDrawWidth();

        this.isLoaded = true;
    }

    onCancel(): void
    {
        // this.toggleEdit.emit();

        // TODO - we are leaving edit, why do we need to trigger a fetch?
        // this.fetch.emit();
        this.cancel.emit();
    }

    onSave(): void
    {
        if (this.form.valid)
        {
            this.isLoaded = false;
            this.infoCard.Metadata = this.metadata;
            this.infoCard.Entity = this.entity;
            const criteria: IInfoCardSaveCriteria = {
                CardProviderId: this.infoCard.CardProviderId,
                InfoCardData: this.infoCard,
                ReturnCriteria: {
                    Criteria: this.returnCriteria,
                    CardProviderId: undefined,
                    Id: undefined,
                },
                DeleteItem: undefined
            };

            this.$infoCardService.Save(criteria)
                .then(
                    (result: IInfoCardData) =>
                    {
                        this.save.emit(result);

                        this.$commonDialogsService.showAlertMessage(
                            "Record has been saved.", "Success");
                    },
                    (error: unknown) =>
                    {
                        if (isHttpResponseError(error) && error.error.AdditionalInfo)
                        {
                            const info: MetadataVerification = JSON.parse(error.error.AdditionalInfo);
                            const dataController = new RepositoryFormData(info.Type, this.entity);

                            this.$metadataErrorService.setErrors(info, dataController);
                            const errors = this.$metadataErrorService.getErrorList(info);

                            return this.$commonDialogsService.showAlertHtmlMessage(
                                errors.reduce((p, x) => `${p}<li>${x}</li>`, ""), "Verification Errors", "Acknowledge");
                        }

                        let msg = "";
                        if (typeof error === "string")
                        {
                            msg = error;
                        }
                        else if (isErroredExecutionResult(error))
                        {
                            msg = error.ErrorMessage;
                        }
                        else if (isExecutionResult(error) && Array.isArray(error.Result) && error.Result.length)
                        {
                            msg = error.Result.join("<br/>");
                        }
                        else if (isHttpResponseError(error))
                        {
                            msg = error.error.Message;
                        }
                        else
                        {
                            console.log(error);
                            msg = "An Error Occurred.";
                        }

                        return this.$commonDialogsService.showAlertHtmlMessage(
                            "Failed to save due to the following: <br/>" + msg,
                            "Save Failed!");
                    }
                )
                .finally(() =>
                {
                    this.isLoaded = true;
                });

            return;
        }
        this.touchForm(this.form.controls);
    }

    touchForm(controls: { [key: string]: AbstractControl<any, any>; }): void
    {
        Object.keys(controls).forEach((control,) =>
        {
            if (controls[control] instanceof FormGroup)
                this.touchForm((<FormGroup>controls[control]).controls);
            else
                controls[control].markAsTouched();
        });
    }

    /** @deprecated - I don't see any place where we use the `cardProviderId` or the literal value of "infoCard" as an element's Id... */
    private setDrawWidth(): number
    {
        // TODO - we should be using ViewChild to find whatever this is... and if its not something we are directly responsibly for, then this is the wrong place for it...
        const id = !document.getElementById(this.infoCard.CardProviderId)
            ? "infoCard"
            : this.infoCard.CardProviderId;

        // TODO - we should be using ViewChild to find whatever this is... and if its not something we are directly responsibly for, then this is the wrong place for it...
        const card = document.getElementById(id);

        const width = card?.clientWidth;
        if (!width || width <= 0 || !this.metadata.Properties)
        {
            return 0;
        }

        // TODO - what are these magic numbers?
        const columns: number = Math.round((width - 300) / 200);
        const denominator = columns <= this.metadata.Properties.length
            ? columns
            : this.metadata.Properties.length;
        const drawWidth = Math.round(100 / (denominator));

        this.metadata.Properties
            .forEach(prop =>
            {
                // TODO - Why are mutating the metadataProperty objects?
                prop.DrawWidth = prop.DrawWidth || drawWidth;
            });
    }
}
